import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../context/AppContext';
import { useConsent } from '../context/ConsentContext';

export default function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const { currentOrganizer, isOrganizer, logout } = useApp();
  const { openPreferences } = useConsent();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Close mobile menu on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileMenuOpen]);

  const isActiveLink = (path) => location.pathname === path;
  const isFullWidthPage = location.pathname === '/for-organizers';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 bg-gradient-to-br from-brand-500 to-brand-600 rounded-lg flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900">openTikt</span>
            </Link>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 -mr-2 rounded-lg hover:bg-gray-50 transition-colors"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center gap-2">
              {isOrganizer ? (
                <>
                  <Link
                    to="/organizer/dashboard"
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/organizer/dashboard')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.dashboard')}
                  </Link>
                  <Link
                    to="/organizer/create-event"
                    className="btn-primary flex items-center gap-1.5 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('nav.newEvent')}
                  </Link>
                  <div className="w-px h-6 bg-gray-200 mx-2" />
                  <button
                    onClick={() => logout()}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('nav.signOut')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/for-organizers"
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/for-organizers')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.forOrganizers')}
                  </Link>
                  <Link
                    to="/login"
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/login')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.login', 'Login')}
                  </Link>
                  <Link
                    to="/organizer/onboard"
                    className="btn-primary text-sm"
                  >
                    {t('nav.startSelling')}
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-100 py-4 px-4 space-y-2" role="menu">
              {isOrganizer ? (
                <>
                  <Link
                    to="/organizer/dashboard"
                    role="menuitem"
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/organizer/dashboard')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.dashboard')}
                  </Link>
                  <Link
                    to="/organizer/create-event"
                    role="menuitem"
                    className="block px-4 py-3 rounded-lg text-sm font-medium text-brand-600 hover:bg-brand-50 transition-colors"
                  >
                    {t('nav.newEvent')}
                  </Link>
                  <button
                    role="menuitem"
                    onClick={() => logout()}
                    className="block w-full text-left px-4 py-3 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors"
                  >
                    {t('nav.signOut')}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/for-organizers"
                    role="menuitem"
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/for-organizers')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.forOrganizers')}
                  </Link>
                  <Link
                    to="/login"
                    role="menuitem"
                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActiveLink('/login')
                        ? 'bg-brand-50 text-brand-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {t('nav.login', 'Login')}
                  </Link>
                  <Link
                    to="/organizer/onboard"
                    role="menuitem"
                    className="block px-4 py-3 rounded-lg text-sm font-medium text-center bg-brand-500 text-white hover:bg-brand-600 transition-colors"
                  >
                    {t('nav.startSelling')}
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className={isFullWidthPage ? '' : 'max-w-6xl mx-auto px-4 sm:px-6 py-8'}>
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 bg-white mt-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-6">
              <Link to="/" className="font-semibold text-gray-900">openTikt</Link>
              <Link to="/for-organizers" className="text-gray-500 hover:text-gray-700">{t('nav.forOrganizers')}</Link>
              <button
                onClick={openPreferences}
                className="text-gray-500 hover:text-gray-700"
              >
                {t('footer.cookieSettings', 'Cookie Settings')}
              </button>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Switcher */}
              <div className="flex items-center gap-1 text-xs">
                {['en', 'es', 'ca'].map((lng) => (
                  <button
                    key={lng}
                    onClick={() => changeLanguage(lng)}
                    className={`px-2 py-1 rounded transition-colors ${
                      i18n.language === lng
                        ? 'bg-brand-100 text-brand-700 font-medium'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t(`language.${lng}`)}
                  </button>
                ))}
              </div>
              <span className="text-gray-300">|</span>
              <p className="text-gray-400">{t('footer.tagline')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
